import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import Navbar from "../components/Navbar";
import TourManagementOptionsSidebar from "../components/tour/TourManagementSidebarOptionsSidebar";
import CustomRadio from "../components/common/CustomRadio";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setOptionsData, setPackageData } from "../reducer/tourReducer";
import TourManagementAvailabilityHeader from "../components/TourManagementAvailabilityHeader";
import { useOptions } from "../hooks/useOptions";
import { resetOptionEdit } from "../reducer/tourEditReducer";

interface RefundEligibility {
  dayRangeMin?: number;
  dayRangeMax?: number;
  percentageRefundable?: number;
}

interface CancelationPolicy {
  policy: string;
  noticePeriod: string;
  type: string;
  description: string;
  cancelIfBadWeather: boolean;
  cancelIfInsufficientTravelers: boolean;
  refundEligibility: RefundEligibility[];
}

// Example Usage
const cancelationPolicy = {
  type: "standard",
  description:
    "Customers are eligible for a full refund if canceled 24 hours before the start time.",
  cancelIfBadWeather: true,
  cancelIfInsufficientTravelers: false,
  refundEligibility: [
    {
      dayRangeMin: 1,
      refundPercentage: 100,
    },
    {
      dayRangeMin: 0,
      dayRangeMax: 1,
      percentageRefundable: 0,
    },
  ],
};

const cancelationType = ["custom", "standard", "all_sales_final"];

const cancelationTypeLabels: { [key: string]: string } = {
  custom: "Custom",
  standard: "Standard",
  all_sales_final: "All Sales Final",
};

// todo: refund eligibility being sent even if it is empty

function TourManagementCancelationPolicy() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { packageData, optionsData } = useSelector(
    (state: RootState) => state.tour
  );
  const {
    tourEdit,
    optionId,
    id: editPackageId,
  } = useSelector((state: RootState) => state.edit);

  useEffect(() => {
    if (location.state && location.state.optionData) {
      dispatch(setOptionsData(location.state.optionData));
    }
  }, [location.state, dispatch]);

  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { createPreOptions } = useOptions();
  const progressref = useRef(19);
  useOnLoadPage({ tabActive: "create" });
  const { PrepackageDataId } = useSelector((state: RootState) => state.tour);

  const handleAddRefundEligibility = () => {
    const newEligibility: RefundEligibility = {
      dayRangeMin: 0,
      dayRangeMax: 0,
      percentageRefundable: 0,
    };
    const updatedEligibility = [
      ...(optionsData.cancelationPolicy?.refundEligibility || []),
      newEligibility,
    ];
    updateCancelationPolicyField("refundEligibility", updatedEligibility);
  };

  // Update a specific refundEligibility entry
  const handleUpdateRefundEligibility = (
    index: number,
    key: string,
    value: number | undefined
  ) => {
    const updatedEligibility = [
      ...(optionsData.cancelationPolicy?.refundEligibility || []),
    ];
    updatedEligibility[index] = {
      ...updatedEligibility[index],
      [key]: value,
    };
    updateCancelationPolicyField("refundEligibility", updatedEligibility);
  };

  // Remove a specific refundEligibility entry
  const handleRemoveRefundEligibility = (index: number) => {
    const updatedEligibility =
      optionsData?.cancelationPolicy?.refundEligibility?.filter(
        (_, i) => i !== index
      ) || [];
    updateCancelationPolicyField("refundEligibility", updatedEligibility);
  };

  // Utility to update cancelationPolicy fields
  const updateCancelationPolicyField = (field: string, value: any) => {
    const updatedPolicy = { ...optionsData.cancelationPolicy, [field]: value };
    dispatch(
      setOptionsData({
        ...optionsData,
        cancelationPolicy: updatedPolicy,
      })
    );
  };

  // const handleContinue = () => {
  //   navigate(Paths.tourManagementPackageOptionsAvailabilityAddOn);
  // };

  const handleSave = async () => {
    const currentPolicy = optionsData.cancelationPolicy || {};
    const newErrors: { [key: string]: string } = {};

    if (!currentPolicy.type || currentPolicy.type.trim() === "") {
      newErrors.type = "Please select a cancellation policy type.";
    }

    if (!currentPolicy.description || currentPolicy.description.trim() === "") {
      newErrors.description = "Please enter a cancellation policy description.";
    }

    if (
      currentPolicy.cancelIfBadWeather !== true &&
      currentPolicy.cancelIfBadWeather !== false
    ) {
      newErrors.cancelIfBadWeather =
        "Please select whether to cancel if bad weather.";
    }

    if (
      currentPolicy.cancelIfInsufficientTravelers !== true &&
      currentPolicy.cancelIfInsufficientTravelers !== false
    ) {
      newErrors.cancelIfInsufficientTravelers =
        "Please select if insufficient travelers affect the trip.";
    }

    const refundEligibility = currentPolicy.refundEligibility || [];
    if (refundEligibility.length === 0) {
      newErrors.refundEligibility =
        "Please add at least one refund eligibility entry.";
    } else {
      const invalidEntry = refundEligibility.some(
        (r) =>
          r.dayRangeMin === undefined ||
          r.dayRangeMax === undefined ||
          r.percentageRefundable === undefined
      );

      if (invalidEntry) {
        newErrors.refundEligibility =
          "Please fill in all refund eligibility fields completely.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const updatedPackageData = {
      ...optionsData,
      _id: optionsData._id,
      progress: Number(progressref.current),
    };

    dispatch(setOptionsData(updatedPackageData));

    const updatedPackageData2 = {
      ...optionsData,
      ...updatedPackageData,
    };

    try {
      if (tourEdit) {
        dispatch(resetOptionEdit());
        dispatch(setOptionsData(updatedPackageData2));
        const pkgId = packageData._id;
        navigate(`/manage/product/${pkgId}/${optionId}`, {
          state: {
            fromEdit: true,
            option: updatedPackageData2,
            packageId: pkgId,
          },
        });
        return;
      } else {
        await createPreOptions(updatedPackageData);
      }
      navigate(Paths.tourManagementPackageOptionsAvailabilityAddOn);
    } catch (error) {
      console.error("Error saving package:", error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  console.log("package", optionsData);

  return (
    <div className={Classname.container}>
      <Navbar />
      <div className={Classname.content}>
        <TourManagementOptionsSidebar selected={5} />

        <div className={Classname.detail}>
          <TourManagementAvailabilityHeader active={4} />
          <div className={Classname.title}>Cancelation Policy</div>
          <div className={Classname.title2}>Cancelation Policy Type</div>
          <div className={Classname.description}>
            Select Your Cancelation Policy Type.
          </div>
          <div className="w-80 mb-4">
            <FormControl fullWidth>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={optionsData.cancelationPolicy?.type}
                onChange={(e) => {
                  let data = {
                    ...optionsData.cancelationPolicy,
                    type: e.target.value,
                  };
                  dispatch(
                    setOptionsData({
                      ...optionsData,
                      cancelationPolicy: data,
                    })
                  );
                }}
              >
                {cancelationType?.map((option, index) => (
                  <MenuItem value={option} key={index}>
                    {cancelationTypeLabels[option] || option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.type && <p className="text-red-500 mt-2">{errors.type}</p>}
          </div>
          <span className={Classname.title2}>
            Cancelation Policy description
          </span>
          <p className={Classname.description}>
            Now, provide your Cancelation description which help to clarify a
            detail.
          </p>
          <textarea
            name=""
            id=""
            cols={30}
            rows={5}
            className={Classname.textarea}
            placeholder="Enter your details"
            value={optionsData.cancelationPolicy?.description}
            onChange={(e) => {
              let data = {
                ...optionsData.cancelationPolicy,
                description: e.target.value,
              };
              dispatch(
                setOptionsData({
                  ...optionsData,
                  cancelationPolicy: data,
                })
              );
            }}
          />

          {errors.description && (
            <p className="text-red-500 mt-2">{errors.description}</p>
          )}

          <div className={Classname.title2}>Is The Weather Condition Bad?</div>

          <CustomRadio
            label="No"
            selected={optionsData.cancelationPolicy?.cancelIfBadWeather}
            value={false}
            onSelect={(value) => {
              let data = {
                ...optionsData?.cancelationPolicy,
                cancelIfBadWeather: false,
              };
              dispatch(
                setOptionsData({
                  ...optionsData,
                  cancelationPolicy: data,
                })
              );
            }}
          />
          <CustomRadio
            label="Yes"
            selected={optionsData.cancelationPolicy?.cancelIfBadWeather}
            value={true}
            onSelect={(value) => {
              let data = {
                ...optionsData?.cancelationPolicy,
                cancelIfBadWeather: true,
              };
              dispatch(
                setOptionsData({
                  ...optionsData,
                  cancelationPolicy: data,
                })
              );
            }}
          />

          {errors.cancelIfBadWeather && (
            <p className="text-red-500 mt-2">{errors.cancelIfBadWeather}</p>
          )}

          <div className={Classname.title2}>
            Do you have Sufficient Travelers?
          </div>

          <CustomRadio
            label="No"
            selected={
              optionsData.cancelationPolicy?.cancelIfInsufficientTravelers
            }
            value={false}
            onSelect={(value) => {
              let data = {
                ...optionsData?.cancelationPolicy,
                cancelIfInsufficientTravelers: false,
              };
              dispatch(
                setOptionsData({
                  ...optionsData,
                  cancelationPolicy: data,
                })
              );
            }}
          />
          <CustomRadio
            label="Yes"
            selected={
              optionsData.cancelationPolicy?.cancelIfInsufficientTravelers
            }
            value={true}
            onSelect={(value) => {
              let data = {
                ...optionsData?.cancelationPolicy,
                cancelIfInsufficientTravelers: true,
              };
              dispatch(
                setOptionsData({
                  ...optionsData,
                  cancelationPolicy: data,
                })
              );
            }}
          />
          {errors.cancelIfInsufficientTravelers && (
            <p className="text-red-500 mt-2">
              {errors.cancelIfInsufficientTravelers}
            </p>
          )}
          <div className="">
            <div className={Classname.title2}>Refund Eligibility</div>

            {(optionsData.cancelationPolicy?.refundEligibility || []).map(
              (eligibility, index) => (
                <div key={index} className="flex gap-4 ">
                  <input
                    type="number"
                    placeholder="Min Days"
                    value={eligibility.dayRangeMin || ""}
                    onChange={(e) =>
                      handleUpdateRefundEligibility(
                        index,
                        "dayRangeMin",
                        parseInt(e.target.value) || undefined
                      )
                    }
                    className={Classname.input}
                  />
                  <input
                    type="number"
                    placeholder="Max Days"
                    value={eligibility.dayRangeMax || ""}
                    onChange={(e) =>
                      handleUpdateRefundEligibility(
                        index,
                        "dayRangeMax",
                        parseInt(e.target.value) || undefined
                      )
                    }
                    className={Classname.input}
                  />
                  <input
                    type="number"
                    placeholder="Refund Percentage"
                    value={eligibility.percentageRefundable || ""}
                    onChange={(e) =>
                      handleUpdateRefundEligibility(
                        index,
                        "percentageRefundable",
                        parseInt(e.target.value) || undefined
                      )
                    }
                    className={Classname.input}
                  />
                  <button
                    onClick={() => handleRemoveRefundEligibility(index)}
                    className={Classname.removeBtn}
                  >
                    Remove
                  </button>
                </div>
              )
            )}

            <button
              onClick={handleAddRefundEligibility}
              className={Classname.addButton}
            >
              Add Refund Eligibility
            </button>
          </div>

          {errors.refundEligibility && (
            <p className="text-red-500 mt-2">{errors.refundEligibility}</p>
          )}

          {/* TODO: MATERIAL UI DROPDOWN */}
          <div className={Classname.buttonsContainer}>
            <button className={Classname.saveButton} onClick={handleBack}>
              Back
            </button>
            <button className={Classname.continueButton} onClick={handleSave}>
              Save and {!tourEdit ? "Continue" : "Exit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default TourManagementCancelationPolicy;

const Classname = {
  container:
    "flex w-full min-h-screen relative bg-white flex-col items-center font-main",
  content:
    "flex flex-col lg:flex-row w-full max-w-7xl flex-1 pt-10 lg:pt-0 max-lg:px-4",
  detail: "flex flex-col py-10 lg:px-6 w-full",
  title: "font-bold text-lg mt-6 mb-2",
  title2: "font-bold lg:text-lg mt-6 mb-2",
  input: "flex w-full h-10 border border-gray-400 rounded-md px-4 mt-4",
  removeBtn:
    "flex w-full h-10 border border-gray-400 rounded-md px-4 items-center mt-4",
  textarea:
    "flex w-full h-44 py-4 border border-gray-400 rounded-md px-4 mt-2 mb-6",
  description: "flex text-base mb-4 mt-2",
  buttonsContainer: "flex flex-1 justify-between lg:justify-end gap-3 mt-10",
  optionButton:
    "font-bold w-fit flex items-center gap-2 cursor-pointer text-primary1 py-3 px-3 border rounded-lg",
  saveButton:
    "h-12 px-6 flex items-center justify-center font-bold text-gray-700 border border-gray-500 rounded-md",
  continueButton:
    "h-12 px-6 flex items-center justify-center text-white font-bold bg-primary1 rounded-md",
  listContainer: "flex flex-wrap gap-4 mt-4",
  listItem:
    "text-primary1 text-sm border border-primary1 px-3 py-1 rounded-md cursor-pointer flex items-center gap-2",
  addButton:
    "bg-primary h-10 px-4 rounded-md flex items-center justify-center text-white font-bold mt-2",
};
